<template>
    <div>
        <Card>
            <template #title>
                Auditoria de Integridade — Financeiro × Contratos
            </template>
            <template #content>
                <Toolbar class="mb-2 bg-gray-100">
                    <template #start>
                        <Tag severity="warn" value="Registros sem vínculo válido com contrato" />
                    </template>
                    <template #end>
                        <div class="flex items-center gap-1">
                            <Button icon="pi pi-search" label="Buscar" severity="success" size="small"
                                @click="toggleBusca" />
                            <Button icon="pi pi-refresh" label="Atualizar" severity="warn" size="small"
                                @click="loadData" />
                        </div>
                    </template>
                </Toolbar>

                <DataTable :value="lista" :loading="isLoading" size="small" stripedRows showGridlines scrollable
                    scrollHeight="calc(100vh - 300px)" :sortField="params.sort.field" :sortOrder="params.sort.order"
                    @sort="onTableSort">

                    <Column style="width: 90px" field="id" header="#" sortable />
                    <Column field="integridade_status" header="Status" sortable>
                        <template #body="{ data }">
                            <Tag :severity="statusSeverity(data.integridade_status)" :value="statusLabel(data.integridade_status)" />
                        </template>
                    </Column>
                    <Column field="id_primario" header="Contrato" sortable>
                        <template #body="{ data }">
                            {{ data.id_primario || '—' }}
                        </template>
                    </Column>
                    <Column field="valor" header="Valor" sortable>
                        <template #body="{ data }">
                            R$ {{ formatMoney(data.valor) }}
                        </template>
                    </Column>
                    <Column field="data_vencimento" header="Vencimento" sortable>
                        <template #body="{ data }">
                            {{ dateFormat(data.data_vencimento) }}
                        </template>
                    </Column>
                    <Column field="cliente" header="Cliente" sortable>
                        <template #body="{ data }">
                            {{ data.cliente || '—' }}
                        </template>
                    </Column>
                    <Column field="justificativa_preco" header="Justificativa">
                        <template #body="{ data }">
                            <span class="line-clamp-2" :title="data.justificativa_preco">
                                {{ data.justificativa_preco || '—' }}
                            </span>
                        </template>
                    </Column>
                    <Column style="width: 80px" bodyClass="flex justify-content-end gap-1">
                        <template #body="{ data }">
                            <Button v-if="permissoes.CONTAS_EDITAR" icon="pi pi-pen-to-square" title="Editar conta"
                                class="p-button-rounded p-button-text p-button-info p-0 w-2rem h-2rem"
                                @click="openForm(data)" />
                        </template>
                    </Column>

                    <template #empty>
                        <div class="text-center text-gray-600">
                            Nenhum registro com falha de integridade
                        </div>
                    </template>
                </DataTable>

                <div class="relative flex align-items-center justify-content-center mt-2 py-1">
                    <span class="absolute left-0 text-sm text-gray-600">{{ pagination.total }} registros</span>
                    <Paginator :first="pagination.first" :rows="pagination.perPage" :totalRecords="pagination.total"
                        @page="onPage" />
                </div>
            </template>
        </Card>

        <Popover ref="busca" :closeOnEscape="false" :dismissable="false" class="w-3">
            <form @submit.prevent="onSubmitBusca">
                <div class="field mt-2">
                    <FloatLabel>
                        <Select id="integridade_status" v-model="params.filter.integridade_status"
                            :options="statusOptions" optionLabel="label" optionValue="value" class="w-full" showClear />
                        <label for="integridade_status">Status de integridade</label>
                    </FloatLabel>
                </div>
                <div class="flex justify-content-end gap-2 mt-4">
                    <Button type="button" label="Limpar" severity="secondary" size="small" @click="clearBusca" />
                    <Button type="submit" label="Aplicar" severity="success" size="small" />
                </div>
            </form>
        </Popover>

        <ContaForm :visible="visibleForm" :data="item" @save="loadData" @close="closeForm" />
    </div>
</template>

<script>
import Card from 'primevue/card';
import Toolbar from 'primevue/toolbar';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import DataTable from 'primevue/datatable';
import Paginator from 'primevue/paginator';
import Column from 'primevue/column';
import Popover from 'primevue/popover';
import FloatLabel from 'primevue/floatlabel';
import Select from 'primevue/select';

import Integridade from '@/service/integridade.js';
import Filter from '@/utils/Filter.js';
import Mask from '@/utils/Mask.js';
import ContaForm from '@/page/conta/Form.vue';

import Moment from 'moment';

export default {
    components: {
        Card,
        Toolbar,
        Button,
        Tag,
        DataTable,
        Paginator,
        Column,
        Popover,
        FloatLabel,
        Select,
        ContaForm,
    },
    data() {
        return {
            permissoes: permissoes,
            lista: [],
            isLoading: false,
            item: {},
            visibleForm: false,
            statusOptions: [
                { label: 'Sem justificativa', value: 'sem_justificativa' },
                { label: 'Sem contrato (com justificativa)', value: 'sem_contrato' },
                { label: 'Contrato inexistente', value: 'contrato_inexistente' },
            ],
            params: {
                filter: {},
                sort: {
                    field: 'id',
                    order: -1,
                },
            },
            pagination: {
                page: 0,
                perPage: 0,
                total: 0,
                first: 0,
            },
        };
    },
    beforeMount() {
        this.pagination.page = parseInt(this.$route.query.page || 0, 10);
        document.addEventListener('onPermissios', event => { this.permissoes = event.permissoes; });
    },
    mounted() {
        this.loadData();
    },
    methods: {
        formatMoney(value) {
            return Mask.money(value);
        },
        dateFormat(value) {
            if (!value) {
                return '—';
            }
            return Moment.utc(value).format('DD/MM/YYYY');
        },
        statusLabel(status) {
            const map = {
                sem_justificativa: 'Sem justificativa',
                sem_contrato: 'Sem contrato',
                contrato_inexistente: 'Contrato inválido',
            };
            return map[status] || status;
        },
        statusSeverity(status) {
            if (status === 'sem_justificativa' || status === 'contrato_inexistente') {
                return 'danger';
            }
            if (status === 'sem_contrato') {
                return 'warn';
            }
            return 'info';
        },
        toggleBusca(event) {
            this.$refs.busca.toggle(event);
        },
        clearBusca() {
            this.params.filter = {};
            this.onSubmitBusca();
        },
        onSubmitBusca() {
            this.pagination.page = 0;
            this.loadData();
            this.$refs.busca.hide();
        },
        onPage(event) {
            this.pagination.page = event.page;
            this.loadData();
        },
        onTableSort(event) {
            this.params.sort.field = event.sortField;
            this.params.sort.order = event.sortOrder;
            this.loadData();
        },
        openForm(data) {
            this.item = { ...data };
            this.visibleForm = true;
        },
        closeForm() {
            this.item = {};
            this.visibleForm = false;
        },
        loadDataParams() {
            let params = [];
            params.push(`page=${1 + this.pagination.page}`);
            params = Filter.formToQuery(this.params.filter, params);
            params.push(`order[${this.params.sort.field}]=` + (this.params.sort.order > 0 ? 'asc' : 'desc'));
            return params;
        },
        loadData() {
            const params = this.loadDataParams().join('&');
            this.isLoading = true;

            Integridade.auditoria('conta', params)
                .then(response => {
                    this.lista = response.data.data;
                    this.pagination.perPage = response.data.per_page;
                    this.pagination.total = response.data.total;
                    this.pagination.first = response.data.per_page * this.pagination.page;
                })
                .catch(error => {
                    const msg = error.response?.data?.msg || error.message;
                    this.$toast.add({ severity: 'error', summary: 'Ops!', detail: msg, life: 5000 });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
    },
};
</script>
